"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRecentMorphoMarkets, type RecentMorphoMarket } from "@/lib/utils/recentSelections";
import { MORPHO_API_URL, MORPHO_SUPPORTED_CHAIN_IDS } from "@/hooks/useMorphoVaultSearch";
export interface MorphoMarket {
  marketId: string;
  chainId: number;
  loanSymbol: string;
  loanAddress: string;
  collateralSymbol: string;
  collateralAddress: string;
  lltvPercent: number;
  borrowApy: number;
  supplyApy: number;
  utilization: number;
  borrowAssetsUsd: number;
  supplyAssetsUsd: number;
}
interface GraphQLMarketItem {
  marketId: string;
  chain?: {
    id: number;
  };
  loanAsset: {
    address: string;
    symbol: string;
  };
  collateralAsset: {
    address: string;
    symbol: string;
  } | null;
  lltv: string;
  state: {
    borrowAssetsUsd: number;
    supplyAssetsUsd: number;
    borrowApy: number;
    supplyApy: number;
    utilization: number;
  };
}
const MARKETS_QUERY = `
query GetMarkets($chainIds: [Int!]!) {
  markets(
    first: 200
    where: {
      chainId_in: $chainIds
      borrowAssetsUsd_gte: 100000
    }
    orderBy: BorrowAssetsUsd
    orderDirection: Desc
  ) {
    items {
      marketId
      chain { id }
      loanAsset { address symbol }
      collateralAsset { address symbol }
      lltv
      state {
        borrowAssetsUsd
        supplyAssetsUsd
        borrowApy
        supplyApy
        utilization
      }
    }
  }
}
`;
const MARKETS_QUERY_BY_LOAN = `
query GetMarketsByLoan($chainIds: [Int!]!, $loanAssets: [String!]!) {
  markets(
    first: 200
    where: {
      chainId_in: $chainIds
      loanAssetAddress_in: $loanAssets
      borrowAssetsUsd_gte: 100000
    }
    orderBy: BorrowAssetsUsd
    orderDirection: Desc
  ) {
    items {
      marketId
      chain { id }
      loanAsset { address symbol }
      collateralAsset { address symbol }
      lltv
      state {
        borrowAssetsUsd
        supplyAssetsUsd
        borrowApy
        supplyApy
        utilization
      }
    }
  }
}
`;
const MARKET_BY_ID_QUERY = `
query MarketById($marketIds: [String!]!, $chainIds: [Int!]!) {
  markets(
    where: {
      id_in: $marketIds
      chainId_in: $chainIds
    }
  ) {
    items {
      marketId
      chain { id }
      loanAsset { address symbol }
      collateralAsset { address symbol }
      lltv
      state {
        borrowAssetsUsd
        supplyAssetsUsd
        borrowApy
        supplyApy
        utilization
      }
    }
  }
}
`;
function parseMarketItems(items: GraphQLMarketItem[], fallbackChainId?: number): MorphoMarket[] {
  return items.filter(item => item.collateralAsset != null).map(item => ({
    marketId: item.marketId,
    chainId: item.chain?.id ?? fallbackChainId ?? 1,
    loanSymbol: item.loanAsset.symbol,
    loanAddress: item.loanAsset.address,
    collateralSymbol: item.collateralAsset!.symbol,
    collateralAddress: item.collateralAsset!.address,
    lltvPercent: Number(item.lltv) / 1e18 * 100,
    borrowApy: item.state.borrowApy,
    supplyApy: item.state.supplyApy,
    utilization: item.state.utilization,
    borrowAssetsUsd: item.state.borrowAssetsUsd,
    supplyAssetsUsd: item.state.supplyAssetsUsd
  }));
}
async function fetchMorphoMarkets(chainId: number | undefined, loanAssetAddress: string | undefined, signal?: AbortSignal): Promise<MorphoMarket[]> {
  const chainIds = chainId ? [chainId] : [...MORPHO_SUPPORTED_CHAIN_IDS];
  const variables: Record<string, unknown> = {
    chainIds
  };
  let query = MARKETS_QUERY;
  if (loanAssetAddress) {
    variables.loanAssets = [loanAssetAddress.toLowerCase()];
    query = MARKETS_QUERY_BY_LOAN;
  }
  const response = await fetch(MORPHO_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      query,
      variables
    }),
    signal
  });
  if (!response.ok) {
    throw new Error(`Morpho API error: ${response.status}`);
  }
  const json = await response.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0].message || "GraphQL error");
  }
  const items: GraphQLMarketItem[] = json.data?.markets?.items ?? [];
  return parseMarketItems(items, chainId);
}
export async function fetchMarketById(marketId: string, chainId?: number): Promise<MorphoMarket | null> {
  const chainIds = chainId ? [chainId] : [...MORPHO_SUPPORTED_CHAIN_IDS];
  const variables = {
    marketIds: [marketId.toLowerCase()],
    chainIds
  };
  const response = await fetch(MORPHO_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({
      query: MARKET_BY_ID_QUERY,
      variables
    })
  });
  if (!response.ok) return null;
  const json = await response.json();
  if (json.errors?.length) return null;
  const items: GraphQLMarketItem[] = json.data?.markets?.items ?? [];
  const markets = parseMarketItems(items, chainId);
  return markets[0] ?? null;
}
interface UseMorphoMarketSearchOptions {
  chainId?: number;
  loanAssetAddress?: string;
  open: boolean;
}
export function useMorphoMarketSearch(t0) {
  const $ = _c(32);
  const {
    chainId,
    loanAssetAddress,
    open
  } = t0;
  const [searchQuery, setSearchQuery] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [allMarkets, setAllMarkets] = useState(t1);
  const [isLoading, setIsLoading] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [recentMarkets, setRecentMarkets] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      status: "idle"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const fetchStatusRef = useRef(t3);
  let t4;
  if ($[3] !== chainId || $[4] !== loanAssetAddress) {
    t4 = () => {
      let recent = getRecentMorphoMarkets();
      if (chainId) {
        recent = recent.filter(m => m.chainId === chainId);
      }
      if (loanAssetAddress) {
        recent = recent.filter(m_0 => m_0.loanAddress?.toLowerCase() === loanAssetAddress.toLowerCase());
      }
      setRecentMarkets(recent);
    };
    $[3] = chainId;
    $[4] = loanAssetAddress;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const refreshRecentMarkets = t4;
  let t5;
  let t6;
  if ($[6] !== chainId || $[7] !== loanAssetAddress || $[8] !== open || $[9] !== refreshRecentMarkets) {
    t5 = () => {
      if (!open) {
        return;
      }
      refreshRecentMarkets();
      const fs = fetchStatusRef.current;
      const isStale = fs.fetchedAt != null && Date.now() - fs.fetchedAt > 300000;
      if ((fs.status === "done" || fs.status === "fetching") && fs.chainId === chainId && fs.loanAssetAddress === loanAssetAddress && !isStale) {
        return;
      }
      const controller = new AbortController();
      fetchStatusRef.current = {
        chainId,
        loanAssetAddress,
        status: "fetching"
      };
      setIsLoading(true);
      fetchMorphoMarkets(chainId, loanAssetAddress, controller.signal).then(result => {
        if (!controller.signal.aborted) {
          setAllMarkets(result);
          fetchStatusRef.current = {
            chainId,
            loanAssetAddress,
            status: "done",
            fetchedAt: Date.now()
          };
        }
      }).catch(err => {
        if (!controller.signal.aborted) {
          console.error("Failed to fetch Morpho markets:", err);
          setAllMarkets([]);
          fetchStatusRef.current = {
            chainId,
            loanAssetAddress,
            status: "error"
          };
        }
      }).finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });
      return () => {
        controller.abort();
        if (fetchStatusRef.current.status === "fetching") {
          fetchStatusRef.current = {
            status: "idle"
          };
        }
      };
    };
    t6 = [open, chainId, loanAssetAddress, refreshRecentMarkets];
    $[6] = chainId;
    $[7] = loanAssetAddress;
    $[8] = open;
    $[9] = refreshRecentMarkets;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[12] !== open) {
    t7 = () => {
      if (!open) {
        setSearchQuery("");
      }
    };
    t8 = [open];
    $[12] = open;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  let markets = allMarkets;
  if (searchQuery) {
    let t10;
    if ($[15] !== markets || $[16] !== searchQuery) {
      const q = searchQuery.toLowerCase();
      t10 = markets.filter(m_1 => m_1.collateralSymbol.toLowerCase().includes(q) || m_1.loanSymbol.toLowerCase().includes(q) || m_1.marketId.toLowerCase().includes(q));
      $[15] = markets;
      $[16] = searchQuery;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    markets = t10;
  }
  if (!searchQuery && recentMarkets.length > 0) {
    let t10;
    if ($[18] !== recentMarkets) {
      t10 = new Set(recentMarkets.map(_temp));
      $[18] = recentMarkets;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    const recentKeys = t10;
    let t11;
    if ($[20] !== markets || $[21] !== recentKeys) {
      let t12;
      if ($[23] !== recentKeys) {
        t12 = m_3 => !recentKeys.has(m_3.marketId.toLowerCase());
        $[23] = recentKeys;
        $[24] = t12;
      } else {
        t12 = $[24];
      }
      t11 = markets.filter(t12);
      $[20] = markets;
      $[21] = recentKeys;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    markets = t11;
  }
  t9 = markets;
  const filteredMarkets = t9;
  let t10;
  if ($[25] !== allMarkets || $[26] !== filteredMarkets || $[27] !== isLoading || $[28] !== recentMarkets || $[29] !== refreshRecentMarkets || $[30] !== searchQuery) {
    t10 = {
      markets: filteredMarkets,
      allMarkets,
      recentMarkets,
      searchQuery,
      isLoading,
      setSearchQuery,
      refreshRecentMarkets
    };
    $[25] = allMarkets;
    $[26] = filteredMarkets;
    $[27] = isLoading;
    $[28] = recentMarkets;
    $[29] = refreshRecentMarkets;
    $[30] = searchQuery;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp(m_2) {
  return m_2.marketId.toLowerCase();
}